import React, { useEffect, useState } from 'react';
import { Check, X, AlertCircle } from 'lucide-react';

interface Props {
  message: string;
  type: 'success' | 'error' | 'info';
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
}

export default function Toast({ message, type, isVisible, onClose, duration = 4000 }: Props) {
  const [isShowing, setIsShowing] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsShowing(true);
      const timer = setTimeout(() => {
        setIsShowing(false);
        setTimeout(onClose, 300); // Wait for animation to complete
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <Check className="w-5 h-5 text-[#0CB4E4]" />;
      case 'error':
        return <X className="w-5 h-5 text-red-400" />;
      case 'info':
        return <AlertCircle className="w-5 h-5 text-[#0CB4E4]" />;
      default:
        return null;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return 'bg-[#161513] border-[#0CB4E4]/30';
      case 'error':
        return 'bg-[#161513] border-red-500/30';
      case 'info':
        return 'bg-[#161513] border-[#0CB4E4]/30';
      default:
        return 'bg-[#161513] border-[#0CB4E4]/30';
    }
  };

  const getTextColor = () => {
    switch (type) {
      case 'success':
        return 'text-white';
      case 'error':
        return 'text-white';
      case 'info':
        return 'text-white';
      default:
        return 'text-white';
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50">
      <div
        className={`
          ${getBackgroundColor()} ${getTextColor()}
          border rounded-lg shadow-lg p-4 max-w-sm
          transform transition-all duration-300 ease-in-out
          ${isShowing ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        `}
      >
        <div className="flex items-center gap-3">
          {getIcon()}
          <p className="text-sm font-medium">{message}</p>
          <button
            onClick={() => {
              setIsShowing(false);
              setTimeout(onClose, 300);
            }}
            className="ml-auto text-gray-400 hover:text-gray-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
